// Types mirroring the FastAPI /api payloads (api/serialize.py::result_to_json and
// the route responses). Kept deliberately loose where the engine result is broad —
// the frontend reads a known subset of keys and treats the rest as passthrough.

import type { EvidenceLegendEntry } from "../lib/matchEvidence";
import type { HandMatchSignRules } from "../lib/handMatchSign";

export interface InsurerInfo {
  name: string;
  multi_file: boolean;
  default_period_from: string | null;
  // GL insurer with no explicit default: fill From from the CRI file's earliest
  // DOC DATE (returned by /api/detect-cri) once that file is chosen.
  derive_period_from_cri: boolean;
  // The OS/GL basis the insurer's layout declares, or null when it declares none.
  // Seeds the basis radio on insurer pick; the user's choice still governs the run.
  cri_basis: "OS" | "GL" | null;
  // Distinctive aliases (trade names / abbreviations) so the picker can shortlist on
  // terms the display name lacks, e.g. "union nationale" -> AL-ITTIHAD AL WATANI.
  aliases: string[];
  // UNVERIFIED testing-tier insurer (not finance-signed-off). The picker badges these
  // so a work-in-progress insurer's figures are never mistaken for a verified one's.
  testing?: boolean;
  // The layout opts in to the step-1 customer variance check (customer roll-forward).
  customer_rollforward?: boolean;
}

// Step 1: each customer's closing in the previous statement against the balance the
// current statement shows at the same date (backend/customer_rollforward.py).
export interface RollforwardFileSummary {
  tie_out: { engine_closing: number; customer_total: number;
             openings_outside_closing: number; difference: number };
  reprints: { dropped: { sheet: string; account: string; rows: number; net: number }[];
              unchained_openings: { sheet: string; account: string; opening: number }[] };
  unread_sheets: { file: string; sheet: string; reason: string; account: string }[];
  sheets: string[];
  // Live rows printed below a customer's closing row: no customer's, counted under a
  // "(below closing row) <tab>" stand-in, and in the statement's closing.
  outside_blocks: { rows: number; net: number };
  // Customer blocks with no printed closing row; `added` when their rows agree with the
  // block's own running balance and so count in the closing.
  unclosed_blocks: { sheet: string; account: string; balance: number;
                     printed: number | null; added: boolean }[];
  // Reports pasted into a tab with other columns: read by neither step, listed here.
  unread_reports?: { file: string; sheet: string; account: string; rows: number; items: number;
                     first_voucher: string; last_voucher: string; from: string; to: string }[];
  // Rows whose printed running balance does not follow from the row above (warn-only).
  balance_breaks?: { rows: number; examples: { sheet: string; account: string; date: string;
                     voucher: string; amount: number; printed: number; previous: number }[] };
  // Rows dated after this statement's own cut-off (listed, never in a balance), and the
  // gap left after tying its balance + those rows to its own customer total (0 = ties).
  after_cut_off: { rows: number; amount: number };
  own_total_difference: number;
}
// One line of "where the variance comes from": a status, then what changed under it.
export interface RollforwardStatusLine {
  status: string;
  variance: number;
  customers: number;
  rows: number;
  changes: { change: string; amount: number; customers: number; rows: number }[];
}
// One partner line (the layout's customer_rollforward.partners): its roll, and every
// uploaded file and tab its customers are read from. The lines sum to the totals.
export interface RollforwardPartnerLine {
  partner: string;
  customers: number;
  not_in_current: number;
  prev_close: number;
  variance: number;
  curr_open: number;
  movement: number;
  curr_close: number;
  found_in: { Previous: string; Current: string };
  places: { statement: "Previous" | "Current"; file: string; sheet: string; customers: number }[];
}
export interface RollforwardSummary {
  previous_cut_off: string;
  current_to: string;
  tolerance: number;
  customers: number;
  status_counts: Record<string, number>;
  with_variance: number;
  net_variance: number;
  abs_variance: number;
  totals: { prev_close: number; curr_open: number; variance: number; movement: number;
            curr_close: number };
  excluded_rows: { Previous: number; Current: number };
  files: { Previous: RollforwardFileSummary; Current: RollforwardFileSummary };
  by_status: RollforwardStatusLine[];
  // null when the layout lists no partners.
  by_partner?: RollforwardPartnerLine[] | null;
  block_columns: string[] | null;
  // Rows in no balance (dated after a cut-off), per statement and reason.
  not_counted: { file: string; reason: string; rows: number; amount: number }[];
}
export interface RollforwardResponse {
  rollforward_id: string;
  customers: FrameRecords;
  // Rows behind each variance; per statement the block columns as that file prints the
  // row, under "prev:<header>" / "curr:<header>" (side_columns names them).
  explaining: FrameRecords;
  excluded: FrameRecords;
  // The current statement customer by customer with a running balance; each customer's
  // last balance is its closing at the To date.
  transactions: FrameRecords;
  summary: RollforwardSummary;
  movement_buckets: string[];
  side_columns: { Previous: string[]; Current: string[] };
}

// A user row from the superadmin panel (/api/admin/users). allowed_insurers null
// means "all shipped insurers" (the default); a list pins the user to a subset.
// The reconciliation books. AP is payable-to-insurers (the original book); AR is
// receivable-from-customers. Mirrors insurer_layout_detector.books.BOOKS.
export type Book = "AP" | "AR";

export const BOOK_LABELS: Record<Book, string> = {
  AP: "Accounts Payable",
  AR: "Accounts Receivable",
};

// An AR customer: `key` scopes its saved matches (the ERP code when the CRI file has
// one), `name` is what the reconciliation is titled with.
export interface ArCustomer {
  key: string;
  name: string;
}

// Who the counterparty is, in the words a finance user already uses. Shown on the book
// chooser: the codes alone do not say which ledger you are about to reconcile, and
// picking the wrong one means a month reconciled against the wrong book's layouts.
export const BOOK_BLURBS: Record<Book, string> = {
  AP: "What we owe insurers",
  AR: "What customers owe us",
};

// One entry of a per-insurer saved-match store, current or removed (GET
// /api/admin/saved-matches). `entry` is the stored JSON exactly as on disk; its shape
// depends on `kind`. who/when are "" for an entry saved before the history existed.
export type SavedMatchKind = "manual" | "carried" | "released" | "rejected";

export interface SavedMatchItem {
  kind: SavedMatchKind;
  insurer: string;
  recon_book: Book;
  customer_key: string;
  status: "active" | "removed";
  entry: Record<string, unknown>;
  saved_by: string;
  saved_at: string;
  removed_by: string;
  removed_at: string;
  // What the entry did in the newest month reconciled (backend.saved_match_runs); null
  // when that run predates it, or for a removed entry or a rejected clear.
  last_run?: SavedMatchRun | null;
  // A manual entry's pinned details (backend/row_pins.py), labelled server-side.
  pins?: { side: "cri" | "insurer"; label: string; value: string }[];
}

export interface SavedMatchRun {
  month: string;      // "YYYY-MM"
  run_at: string;
  run_by: string;
  applied: boolean;
  status: string;
  reason: string;
}

export interface AdminUser {
  username: string;
  role: string;
  disabled: boolean;
  allowed_insurers: string[] | null;
  // Which reconciliation books this account may work in. null/absent means the DEFAULT
  // book only (AP) — what every account created before the AR book existed reads as, so
  // shipping AR never silently widens one.
  allowed_books?: Book[] | null;
  // May reconcile an uncached (new) insurer, which triggers a paid Gemini detection.
  // Optional so a legacy user doc without the field reads as undefined (treated false).
  can_use_gemini?: boolean;
  // May select UNVERIFIED testing-tier insurers. Optional so a legacy doc reads false.
  can_use_testing_insurers?: boolean;
}

// A DataFrame serialised as a capped record set (api/serialize.py::_frame_to_records).
// `indices` is the original pandas index per row (parallel to rows), used to key
// manual notes by the same {row_index: text} the engine's stamp_manual_notes expects.
export interface FrameRecords {
  columns: string[];
  rows: (string | number | boolean | null)[][];
  indices: (number | string | null)[];
  total_rows: number;
}

// The reconciliation summary dict (subset the UI reads; other keys pass through).
export interface ReconSummary {
  difference: number;
  is_reconciled?: boolean;
  matched_count?: number;
  mismatch_count?: number;
  cri_only_count?: number;
  insurer_only_count?: number;
  sub_total?: number;
  [k: string]: number | boolean | string | null | undefined;
}

// A carried item a later row of the other book cleared (backend carried_cleared_after).
export interface CarriedClearedAfter {
  book: "cri" | "insurer";
  item: number;
  clearing_side: "cri" | "insurer";
  clearing_index: number;
  cleared_in: string;
  amount: number;
  track: string;
  evidence: "reference" | "policy" | "manual";
  // Every row a hand pair cleared with, when it named more than one.
  clearing_legs?: ["cri" | "insurer", number][];
}

// Prior-period unreconciled items (GL only). Null/absent unless finance uploaded the
// carried-forward file. The Closing Balance is unchanged; `adjusted_closing` states
// the position once the items still OPEN after this period are allowed for.
export interface UnreconciledBlock {
  rows: FrameRecords;
  matched_total: number;
  open_total: number;
  matched_count: number;
  open_count: number;
  // Rows the insurer's layout classified as decorative (a total/section line). They
  // are NOT items and never move the adjusted figure, but they are reported so
  // settled + open + excluded always equals every row in the file.
  excluded_count: number;
  excluded_total: number;
  // Rows whose reference reached us as a number too large for a float to hold exactly
  // (>2^53), so Excel dropped its low-order digits before any reader saw the cell.
  // Those items cannot be presence-tested and correctly stay open — reported so the
  // reason is visible rather than the row appearing open for no stated cause.
  precision_damaged_count?: number;
  // Items brought forward from the previous month's output that it showed with ONLY
  // their reference/policy/date/narration/name/amount, every other column gone — how a
  // workbook exported before 2026-10-01 wrote an item it had itself brought forward.
  // Advisory: no figure depends on it.
  rolled_key_only_count?: number;
  // The columns finance's own file carried, in file order. The table renders exactly
  // these (plus the settled-against evidence) so it reads like the uploaded file
  // rather than exposing the parse's derived/bookkeeping columns.
  source_columns?: string[];
  // Items currently held open by a human override (a rejected auto-clear). Reversible:
  // restoring one lets the tier ladder clear it again on the next run.
  rejected_count?: number;
  // Items settled by a MANUAL pair rather than by the tier ladder.
  manual_count?: number;
  // What each STORED manual pair did this run. A pair has exactly three outcomes and
  // they need different action from the user, so they are reported separately rather
  // than as one "didn't work" bucket:
  //   manual_applied    — it settled the item
  //   manual_untied     — both legs resolved but the amounts missed; item stays OPEN
  //                       and is annotated with the gap (a failed pair never moves the
  //                       Adjusted Closing)
  //   manual_unresolved — it applied NOTHING; `reason` says why
  // The third is silent unless rendered — the defect the main Manual Match tab hit
  // with its own manual_unresolved.
  manual_applied?: CarriedMatchOutcome[];
  manual_untied?: CarriedMatchOutcome[];
  manual_unresolved?: CarriedMatchUnresolved[];
  insurer_closing: number;
  adjusted_closing: number;
  // [book, ledger row] of each "open before the period, cleared after" pairing whose
  // carried copy moved off Group 1/2 onto the Cleared line (finance 2026-09-25).
  cleared_after_rows?: ["cri" | "insurer", number][];
  // Carried items with no ledger row of their own that a row of the OTHER book dated
  // after the period clears (backend carried_cleared_after), and items a hand pair cleared
  // against rows dated after the period (evidence "manual"). Also on the Cleared line.
  cleared_after_items?: CarriedClearedAfter[];
  // Still-open claims on the insurer list (rows flagged unrec_claim), listed on the Other
  // tab and counted on the Others line instead of Group 1 (finance 2026-10-16).
  claims_total?: number;
  claims_count?: number;
  // The CRI side's own carried population (finance 2026-08-25). Prior-period breaks
  // exist on BOTH books, so the CRI ledger carries its own list and its own adjusted
  // closing. cri_rows is null on an insurer-only upload, where every figure below is 0
  // — so a consumer that ignores these keys behaves exactly as it did before.
  cri_rows?: FrameRecords | null;
  cri_source_columns?: string[];
  cri_matched_total?: number;
  cri_open_total?: number;
  cri_matched_count?: number;
  cri_open_count?: number;
  cri_excluded_count?: number;
  cri_excluded_total?: number;
  cri_precision_damaged_count?: number;
  cri_rolled_key_only_count?: number;
  cri_closing?: number;
  cri_adjusted_closing?: number;
  // What each CRI-BOOK manual pair did this run. The unprefixed keys above report the
  // INSURER book's pairs only, so without these a CRI-book pair had no outcome anywhere
  // and the tab could only say "not applied this run" about one that had settled.
  cri_manual_applied?: CarriedMatchOutcome[];
  cri_manual_untied?: CarriedMatchOutcome[];
  cri_manual_unresolved?: CarriedMatchUnresolved[];
  cri_manual_count?: number;
  cri_rejected_count?: number;
  // The rules the engine judged every hand pair by, so the match bar previews exactly them.
  manual_rules?: CarriedManualRules;
}

// backend/carried_match.py::_manual_rules. One set for both lists.
export interface CarriedManualRules {
  // The insurer's declared cross_frame_sign between the ledgers; null when no layout
  // states one, and then the two legs are compared at magnitude.
  sign_frame: "inverted" | "same" | null;
  // Tracks the main run matches at magnitude (lower-cased).
  abs_tracks: string[];
  tol: number;
  tol_by_track: Record<string, number> | null;
  // The open buckets a settling row may come from, per ledger.
  open_categories: { cri: string[]; insurer: string[] };
}

// One stored carried pair that RESOLVED: `tied` says whether it settled the item or
// missed on amount (in which case `note` carries the engine's own annotation). `pair` is
// the saved pair that did it, so an outcome is read per pair, never per item.
export interface CarriedMatchOutcome {
  carried_sig: string;
  tied: boolean;
  side?: string;
  label?: string;
  note?: string;
  pair?: CarriedMatchPair;
  // Tied against rows dated after the period: the item stays open and is listed on the
  // Cleared in Subsequent Month line (backend carried_match._record_later).
  cleared_after?: { item: number; rows: number[]; cleared_in: string };
}

// One stored carried pair that applied nothing at all, with the engine's verbatim
// reason. `pair` is echoed back so the UI can offer to forget exactly that entry.
export interface CarriedMatchUnresolved {
  pair: CarriedMatchPair;
  reason: string;
}

// One book's side of ReconResult.prior_output_opening_gap.
export interface PriorOutputBook {
  previous_closing: number;
  opening: number;
  listed_before_period: number;
  difference: number;
}

// The full result payload from /api/reconcile and /api/finalize (result_to_json).
export interface ReconResult {
  summary: ReconSummary;
  unreconciled?: UnreconciledBlock | null;
  // Set only when a prior-period file WAS supplied and could not be read. Without it
  // a failed upload renders exactly like no upload at all.
  unreconciled_error?: string | null;
  df_cri: FrameRecords;
  df_insurer: FrameRecords;
  // Manual-match apply path only: the Tier-6 rows instead of the whole frame (the
  // server sends these in place of df_cri/df_insurer when frames_delta is set).
  // applyFramesDelta patches them into the held frames — see lib/framesDelta.ts.
  df_cri_delta?: FrameRecords;
  df_insurer_delta?: FrameRecords;
  // With a delta: whether this run's Match Group labels are on (they are run-wide).
  match_groups_labelled?: boolean;
  summary_df?: FrameRecords;
  cri_closing?: number;
  insurer_closing?: number;
  cri_closing_combined?: number;
  insurer_closing_combined?: number;
  insurer_opening?: number | null;
  // False when the statement stated no opening at all (display only; see the card).
  insurer_opening_detected?: boolean;
  insurer_name?: string;
  insurer_sheet?: string;
  // Which BOOK this result was run in, and (AR only) WHICH CUSTOMER. Together they
  // scope every per-insurer store call the dashboard makes — remembered matches,
  // released matches, carried pairs, rejections. Absent on a result produced before the
  // book axis existed, which reads as AP with no customer.
  recon_book?: Book;
  customer_key?: string;
  customer_name?: string;
  // Display segments (backend/segments.py). `segments` lists the switch's options and is
  // absent for an insurer declaring none; the rest describe the slice this result shows
  // and are absent on All business.
  segments?: { key: string; label: string }[];
  segment?: string;
  segment_label?: string;
  segment_note?: string | null;
  // Policy-numbered CRI rows matched to an insurer row of the other segment.
  segment_cross_pairs?: number;
  cri_rows?: number;
  insurer_rows?: number;
  track_closings?: Record<string, Record<string, number>>;
  track_summary?: TrackSummaryRow[];
  group_breakdown?: GroupBreakdownRow[];
  cri_closing_total?: number;
  insurer_closing_total?: number;
  mode?: string;
  period_start?: string | null;
  period_end?: string | null;
  // The window's From actually in force (the user's, else the CRI floor); null = none.
  period_from?: string | null;
  as_of_date?: string | null;
  merged_accounts?: string[];
  // Ingested sheets naming a prior calendar year (opt-in schemas only, e.g. Adamjee
  // medical whose only period signal is the sheet name). Advisory; rows still reconciled.
  prior_year_sheets?: string[];
  // Uploaded insurer files no reader could open (multi-file path). Their rows are in no
  // total, so the result page raises a critical banner. Empty otherwise.
  unreadable_files?: string[];
  // Converted statements (GL listing PDF/HTML) that were reconciled but do not tie to
  // their own printed figures. Critical banner. Empty when every statement foots.
  statement_warnings?: { file: string; message: string }[];
  // The run's own figures that break an identity they must satisfy
  // (reporting/self_check.py). Critical banner. Empty on a clean run.
  self_check?: { check: string; track: string; amount: number; bound: number }[];
  // Expected column headers absent (or duplicated) at the resolved header row of a
  // variant SOA; reconciliation still ran on the columns present. Advisory.
  header_warning?: string[];
  // Reference-join coverage (informational, NOT an integrity warning): a companion
  // lookup file supplied an id column but did not cover every statement row. The
  // unresolved rows keep a blank reference and match on the other tiers, so no
  // figure is affected. Empty unless such a join ran and left rows unresolved.
  reference_warning?: { filled: number; unresolved: number }[];
  // Non-blank amount cells (either ledger) that failed numeric parsing and were
  // treated as 0.00 — totals may be understated. One entry per source column/path.
  amount_warning?: { context: string; count: number; samples: string[] }[];
  // Rows a skip rule removed that carry a document number and money: possibly real
  // transactions, counted in no figure. Critical banner. Empty for a clean statement.
  skip_warning?: { context?: string; count: number;
                   samples: { row: number; id: string; amount: number }[] }[];
  // The layout's stated sign frame was kept over a confident data-derived one. Critical
  // banner: one of the two is wrong. Empty on a clean run.
  sign_frame_warning?: { declared: "same" | "inverted"; data_frame: "same" | "inverted";
                         n_pairs: number }[];
  // Non-destructive duplicate-sheet warnings from the multi-file merge; each names
  // the two "file [sheet]" members of a suspected duplicated ledger. All sheets
  // were still merged. Empty otherwise.
  merged_warnings?: {
    sheet_a: string;
    sheet_b: string;
    overlap: number;
    shared_keys: number;
  }[];
  // Chain-integrity warning (merge_mode="chain" only): set when the uploaded month
  // snapshots provably do NOT form one contiguous run, so the headline closing fell
  // back to a sum that double-counts the shared openings. null when clean.
  chain_warning?: {
    kind: "duplicate" | "gap";
    n_files: number;
    openings: number[];
    closings: number[];
  } | null;
  // Set when a previous month's output was uploaded and this run does not open where it
  // closed: walkdown_effect is what that puts between the Overview's Gross Variance and
  // Net Adjustment. `difference` is the part of opening - previous_closing no row states
  // (listed_before_period is already on Matched). GL display signs. UI only - never
  // exported. null otherwise.
  prior_output_opening_gap?: {
    insurer: PriorOutputBook;
    cri: PriorOutputBook;
    not_carried: number;
    previous_walkdown_gap: number;
    walkdown_effect: number;
  } | null;
  // The previous output's Others rows carried onto this run's Others tab and Overview
  // Others line (backend carried_read.read_previous_output_others). Rows under the
  // headers that export showed; total/count are the engine's. null without any.
  prior_others?: PriorOthers | null;
  // prior_others kept unchecked against the previous output's Cleared tab (that check
  // failed), so an item on both may be brought forward twice.
  prior_others_not_deduplicated?: boolean;
  // Frame indices, per book, of the rows dated before the period that no uploaded
  // prior-period list carries (backend carried_rolled.unlisted_pre_period_rows).
  unlisted_pre_period_rows?: { cri?: (number | string)[]; insurer?: (number | string)[] };
  // Those of them the previous output's Statement tab never saw (late postings); a book is
  // absent when its tab could not be read (backend carried_rolled.late_posted_pre_period_rows).
  late_posted_pre_period_rows?: { cri?: (number | string)[]; insurer?: (number | string)[] };
  display_config?: DisplayConfig;
  note_suggestions?: string[];
  // The Matched By vocabulary (label, glyph, evidence kind, plain sentence) in
  // legend order, from reporting/match_evidence.py — the SAME definition the
  // workbook writes its legend band from. `rows` is 0 here: the browser counts the
  // slice it renders, so a legend always foots to the grid under it.
  match_evidence_legend?: EvidenceLegendEntry[];
  // Key-confirmed near-misses for the manual-match "Suggested matches" queue: pairs
  // that share a real reference/policy key and reconciling sign but were left open on
  // the amount (just outside tolerance). Advisory shortcut for hand-confirming; never
  // a reconciliation input. Empty when nothing qualifies.
  near_misses?: NearMiss[];
  // Manual matches (remembered or session) the engine could NOT apply this run, with
  // the reason per leg. A remembered pair stops resolving as soon as its row settles,
  // gets auto-matched, or changes figures — expected over a month, but silent unless
  // it is shown, so the Manual Match tab surfaces it.
  manual_unresolved?: ManualUnresolved[];
  // Saved matches whose rows rolled forward into the prior-period lists and which applied
  // to those items instead (backend carried_match.rolled_manual_matches).
  manual_rolled?: ManualRolled[];
  // How a cross-ledger hand match must cancel (backend hand_match_rules); the match
  // bar and the applied-match footing judge by it, as the engine does.
  manual_match_rules?: HandMatchSignRules;
  // Stored releases that freed NOTHING this run (backend _unapplied_releases): a Release
  // reverses only the app's own matches, so one on a hand-made or prior-period match is
  // inert. Listed so it can never read as "Released".
  released_unapplied?: ReleasedUnapplied[];
  // Broad engine result — any other scalar/dict/list keys are carried through.
  [k: string]: unknown;
}

// One stored release that freed nothing this run, and what holds the row instead.
export interface ReleasedUnapplied {
  signature: string;
  side: string;
  kind: "manual" | "prior" | "none" | "ambiguous" | "rekeyed";
  reason: string;
}

// One manual-match leg the engine refused to apply (backend _apply_manual_matches).
// `signature` is the content signature that failed to resolve, `side` names the ledger
// ("CRI" / "Insurer", or "same-book" / "group" for a whole-entry rejection).
export interface ManualUnresolved {
  signature: string;
  side: string;
  reason: string;
}

// `pair` as stored; `settled` when every leg settled (else the items sit on Group 3).
export interface ManualRolled {
  pair: ManualMatchPair;
  settled: boolean;
}

// One suggested pair for the manual-match queue (backend _resolve_near_misses). cri_sig
// / insurer_sig are the same content signatures the Accept flow sends back as a manual
// match; amounts + gap drive the row's display and the Matched/Mismatch preview.
export interface NearMiss {
  cri_sig: string;
  insurer_sig: string;
  tier: number;
  reason: "reference" | "policy";
  cri_amount: number;
  insurer_amount: number;
  amount_gap: number;
}

// One line of the per-track summary (app.bridge._compute_track_summary): CRI Closing,
// Insurer SOA Closing Balance, Unreconciled Variance, and the 4 group nets+counts.
export interface TrackSummaryRow {
  Track: "Premium" | "Commission";
  Line: string;
  "Amount (AED)": number;
  Count: number | "";
}

// One subtype row of the group drill-down (reporting.bridge._compute_group_breakdown).
// Indices = original frame index of every transaction in the bucket (frame row order),
// letting the dashboard list the bucket's rows without re-deriving membership.
export interface GroupBreakdownRow {
  Track: "Premium" | "Commission";
  Group: string;
  Side: "CRI" | "Insurer";
  Subtype: string;
  "Amount (AED)": number;
  Count: number;
  Indices?: (number | string | null)[];
}

// The app's curated display config per side (app.filters._get_display_config): the
// lead column order + friendly header renames, used to declutter the grids to the
// same concise column set the Streamlit statement tabs show.
export interface SideDisplayConfig {
  columns: string[];
  renames: Record<string, string>;
  // Raw SOA amount columns (reporting/filters.py::raw_money_columns), printed as money.
  money_columns?: string[];
}
export interface DisplayConfig {
  cri: SideDisplayConfig;
  insurer: SideDisplayConfig;
}

// Section taxonomy for the residual "Other" population (reporting/other_sections.py).
// Titles, hints and the in/not-in-Difference flags are DEFINED SERVER-SIDE and shared
// with the Excel Others sheet, so the two surfaces cannot describe the residual
// differently. The browser renders these strings; it never decides them.
export interface OtherSections {
  order: string[];
  titles: Record<string, string>;
  hints: Record<string, string>;
  in_difference: Record<string, boolean>;
  badge_in: string;
  badge_not_in: string;
  post_period_note: string;
  // Cross-cutting "every undated row" reference view. Its key is deliberately NOT in
  // `order` — it overlaps the residual sections rather than partitioning with them.
  // Optional: an older payload predates it and the tab falls back to literals.
  undated_all_key?: string;
  undated_all_title?: string;
  undated_all_hint?: string;
}

// ReconResult.prior_others: the previous output's carried Others, per book.
export interface PriorOthers {
  cri: FrameRecords | null;
  insurer: FrameRecords | null;
  total: number;
  count: number;
  // The same rows for the Manual Match books, with unrec_sig / balance_amount and the
  // unrec_prior_others marker (lib/priorPeriodMatch.withPriorOthers).
  match?: Partial<Record<"cri" | "insurer", FrameRecords>>;
}

export interface ReconcileResponse {
  prematch_id: string;
  result: ReconResult;
}

export interface FinalizeResponse {
  result: ReconResult;
}

// A manual match is a GROUP of rows the user asserts settle each other. Three shapes
// are accepted, all handled by one backend path:
//   * {cri_sig, insurer_sig}        — 1:1 cross-book (one CRI row + one insurer row)
//   * {a_sig, b_sig}                — same-book offset (two rows in ONE ledger)
//   * {cri_sigs, insurer_sigs}      — the general N:M group, which subsumes both
// The kind is read off which fields are set, never a flag, so a match can't be stored
// as one kind and read back as another. The tie gate runs on the WHOLE group: if the
// two sides tie within tolerance every leg is Matched, otherwise every leg is Mismatch
// (Figure/Balance Mismatch) — all-or-nothing, which is what keeps the Difference honest.
export interface ManualMatchPair {
  cri_sig?: string;
  insurer_sig?: string;
  a_sig?: string;
  b_sig?: string;
  cri_sigs?: string[];
  insurer_sigs?: string[];
  // Applied records only (result.manual_matched): the stamped rows' CURRENT keys, which
  // differ from the saved keys above for a match saved under a retired row key.
  cri_keys?: string[];
  insurer_keys?: string[];
  // Saved entries only: per leg key, the detail finance said tells that row apart from
  // its look-alikes (backend/row_pins.py). Never part of the entry's identity.
  pins?: Record<string, RowPin>;
}

export interface RowPin {
  column: string;
  value: string;
}

// The save dialog's questions for one pair (backend/manual_save_check.py).
export interface SaveCheck {
  conflicts: ManualMatchPair[];
  lookalikes: LookalikeLeg[];
}

export interface LookalikeLeg {
  side: "cri" | "insurer";
  key: string;
  labels: Record<string, string>;
  rows: { yours: boolean; values: Record<string, string> }[];
  // Only the details that alone tell the ticked row apart from every look-alike.
  columns: { column: string; label: string; yours: string; others: string[] }[];
}

// One auto-match finance has RELEASED back to the open buckets so it can be re-paired by
// hand (backend/released_match_store.py). `sigs` is every leg of the released match —
// both sides always, since freeing one and leaving its partner Matched would strand the
// partner. Releasing NEVER moves the Difference: the legs land in buckets that cancel in
// the summary identity, so it re-classifies rows without touching the bottom line.
export interface ReleasedGroup {
  sigs: string[];
  reason: string;
}

// A manual match on a PRIOR-PERIOD carried item. Separate from ManualMatchPair because
// the two never mix: a carried pair is resolved outside the engine frames and moves only
// the Adjusted Closing Balance, so it carries the carried row's own signature
// (`unrec_sig`) rather than an engine `_sig`.
//   { carried_sig, row_sig, side }  — carried item against one current-period row
//   { carried_sig, row_sigs, side } — carried item against 2+ rows on one ledger
//   { carried_sigs: [...] }         — 2+ carried items that cancel each other
//   { insurer_carried_sig, cri_carried_sig } — one item on EACH carried list (book "both")
//   { insurer_carried_sigs, cri_carried_sigs } — several items across both lists (N:M)
//   { basket: { items, rows } } — items with rows on both ledgers, or items on both lists
//                                  with rows (backend carried_basket._resolve_basket)
//
// `book` and `side` are DIFFERENT axES and both are needed: `book` is which carried list
// the ITEM is on, `side` is which ledger its SETTLING transaction sits on. A CRI-book
// item settled by an insurer-ledger row is book:"cri", side:"insurer". Absent means the
// insurer list — the only one pairs could be made on before the field existed.
export interface CarriedMatchPair {
  carried_sig?: string;
  row_sig?: string;
  row_sigs?: string[];
  side?: "cri" | "insurer";
  carried_sigs?: string[];
  insurer_carried_sig?: string;
  cri_carried_sig?: string;
  insurer_carried_sigs?: string[];
  cri_carried_sigs?: string[];
  basket?: CarriedBasket;
  book?: "cri" | "insurer" | "both";
  // On an outcome only: the prior-period items a match's rows rolled forward into, per
  // list (backend carried_rolled). Never stored.
  rolled?: Partial<Record<"cri" | "insurer", string[]>>;
  // On an outcome only: the saved MAIN-tab match this pair was made from when its rows
  // rolled forward (backend carried_rolled.MAIN_PAIR); it lives in the main store.
  main_pair?: ManualMatchPair;
}

// A basket's prior-period items per list and this period's rows per ledger.
export interface CarriedBasket {
  items: Partial<Record<"cri" | "insurer", string[]>>;
  rows: Partial<Record<"cri" | "insurer", string[]>>;
}

// One stored note on a prior-period item (/api/carried-notes).
export interface CarriedNote {
  list: "cri" | "insurer";
  sig: string;
  note: string;
}

// The items a cross-list pair names on each list (1:1 or N:M); empty for any other
// shape. Mirrors backend/carried_rolled.py::cross_list_sigs.
export function crossListSigs(p: CarriedMatchPair): { ins: string[]; cri: string[] } {
  if (p.insurer_carried_sig && p.cri_carried_sig) {
    return { ins: [p.insurer_carried_sig], cri: [p.cri_carried_sig] };
  }
  const ins = (p.insurer_carried_sigs ?? []).filter(Boolean);
  const cri = (p.cri_carried_sigs ?? []).filter(Boolean);
  return ins.length && cri.length ? { ins, cri } : { ins: [], cri: [] };
}

// True for a pair naming items on EACH carried list.
export function isCrossListPair(p: CarriedMatchPair): boolean {
  return crossListSigs(p).ins.length > 0;
}

// Which carried list a pair belongs to. ONE reader, mirroring
// backend/carried_manual_store.py::book_of, so the default cannot drift between them.
export function carriedBookOf(p: CarriedMatchPair): "cri" | "insurer" | "both" {
  if (p.basket) {
    const { insurer, cri } = p.basket.items;
    return insurer?.length && cri?.length ? "both" : cri?.length ? "cri" : "insurer";
  }
  if (isCrossListPair(p)) return "both";
  return p.book === "cri" ? "cri" : "insurer";
}

// The current-period legs of a cross pair (one or several), de-duplicated in order.
export function carriedRowSigs(p: CarriedMatchPair): string[] {
  const rows = (p.row_sigs ?? []).filter(Boolean);
  return [...new Set(rows.length ? rows : p.row_sig ? [p.row_sig] : [])];
}

// True when the entry is the general N:M group shape.
export function isGroupPair(p: ManualMatchPair): boolean {
  return Boolean(p.cri_sigs?.length || p.insurer_sigs?.length);
}

// Every leg, split by ledger — the one accessor all display code should use.
export function groupSigs(p: ManualMatchPair): { cri: string[]; ins: string[] } {
  if (isGroupPair(p)) {
    return { cri: p.cri_sigs ?? [], ins: p.insurer_sigs ?? [] };
  }
  if (p.a_sig || p.b_sig) {
    const legs = [p.a_sig ?? "", p.b_sig ?? ""].filter(Boolean);
    const ins = legs[0]?.startsWith("INS|");
    return { cri: ins ? [] : legs, ins: ins ? legs : [] };
  }
  return { cri: p.cri_sig ? [p.cri_sig] : [], ins: p.insurer_sig ? [p.insurer_sig] : [] };
}

// True when every leg sits in ONE ledger (a within-CRI / within-insurer offset).
export function isSameBookPair(p: ManualMatchPair): boolean {
  const { cri, ins } = groupSigs(p);
  return cri.length === 0 || ins.length === 0;
}

// The two signatures of a 1:1/2-leg pair — for display and identity. Retained for the
// two-leg display paths; groupSigs is the general accessor.
export function pairSigs(p: ManualMatchPair): [string, string] {
  const { cri, ins } = groupSigs(p);
  const legs = [...cri, ...ins];
  return [legs[0] ?? "", legs[1] ?? ""];
}

// Stable identity key for a match (used for selection, dedupe and removal). Mirrors the
// backend's manual_match_store.pair_key: CRI legs then insurer legs, so a 2-leg group
// and the equivalent pair stored the old way are ONE identity.
export function pairKey(p: ManualMatchPair): string {
  const { cri, ins } = groupSigs(p);
  return [...cri, ...ins].join("⟷");
}

// True when the carried entry is the carried-to-carried offset shape (2+ carried items
// that cancel each other), as opposed to carried item(s) against current-period rows.
export function isCarriedOffset(p: CarriedMatchPair): boolean {
  return (p.carried_sigs?.length ?? 0) >= 2 && !carriedRowSigs(p).length;
}

// Stable identity for a carried entry — for dedupe and removal. MIRRORS
// backend/carried_manual_store.py::pair_key, including its sorted-set rule for groups:
// the same two carried items named in either order are ONE pair, never two. A key that
// disagreed with the server's would make Forget silently no-op.
export function carriedPairKey(p: CarriedMatchPair): string {
  if (p.basket) {
    const b = p.basket;
    return ["basket", ...[b.items, b.rows].flatMap((part) => (["insurer", "cri"] as const)
      .flatMap((s) => [...[...(part[s] ?? [])].filter(Boolean).sort(), "|"]))].join("⟷");
  }
  if (isCrossListPair(p)) {
    const { ins, cri } = crossListSigs(p);
    if (ins.length > 1 || cri.length > 1) {
      return ["xlistN", ...[...ins].sort(), "|", ...[...cri].sort()].join("⟷");
    }
    return ["xlist", ins[0], cri[0]].join("⟷");
  }
  const book = carriedBookOf(p);
  if (isCarriedOffset(p)) {
    return ["group", book, ...[...(p.carried_sigs ?? [])].sort()].join("⟷");
  }
  const rows = carriedRowSigs(p);
  if ((p.carried_sigs?.length ?? 0) >= 2) {
    return ["groupN", book, p.side ?? "", ...[...(p.carried_sigs ?? [])].sort(), "|",
            ...[...rows].sort()].join("⟷");
  }
  if (rows.length >= 2) {
    return ["crossN", book, p.carried_sig ?? "", p.side ?? "", ...[...rows].sort()].join("⟷");
  }
  return ["cross", book, p.carried_sig ?? "", rows[0] ?? "", p.side ?? ""].join("⟷");
}

// Settings the sidebar collects and the reconcile/export calls consume.
export interface ReconSettings {
  tolerance: number;
  amount_match_tol: number;
  // Per-track Matched tolerance (mirrors the Streamlit sidebar: premium /
  // commission; claims reuse commission server-side).
  amount_tol_premium?: number;
  amount_tol_commission?: number;
  // USD->AED rate applied to USD-carrying insurer statements before matching.
  usd_rate?: number;
  // EUR->AED rate applied to EUR-carrying insurer statements before matching.
  eur_rate?: number;
  mode_override?: string;
  cri_sheet_override?: string;
  period_from?: string;
  // The user cleared a From that had a value: drop the engine's cri_period_floor
  // default so the whole ledger is reconciled. See SidebarSettings.periodFromCleared.
  period_from_cleared?: boolean;
  period_to?: string;
  // "YYYY-MM" — the month the reconciliation is FOR. Resolves server-side to that
  // month-end and becomes the engine's as-of cutoff, which every "as at" label (the
  // Excel header included) reports. Absent/"" = Automatic: no override at all, so the
  // run is byte-identical to one made before this field existed.
  recon_month?: string;
}

// One candidate sheet from /api/preview-multi (backend.preview_multi_file_matches):
// which uploaded sheets the multi-file merge will ingest.
// One sheet of a prior-period unreconciled workbook, as read by ONE side. Finance
// ships both books in one workbook, so a sheet that is perfect for the insurer side is
// correctly unusable on the CRI side: `ok: false` with a `reason` is the normal report
// for the other book's tab, not a failure.
export interface CarriedSheet {
  sheet: string;
  // Items the reconciliation would carry from this sheet (totals/footers excluded).
  items: number;
  ok: boolean;
  // Why this side cannot use the sheet; "" when it can.
  reason: string;
}

export interface MultiPreviewEntry {
  fname: string;
  sheet: string;
  matched: boolean;
  account: string | null;
  // Pre-tick hint only (absent on older responses = tick it). False for a matched
  // month tab superseded by a newer month in the same account, or a sheet whose every
  // document is also on `superseded_by`; the sheet is still listed and the user can
  // tick it back on.
  default_selected?: boolean;
  // The sheet in the same workbook + account that already holds every document on
  // this one (AL-ITTIHAD's GL for its OS tab). Absent otherwise.
  superseded_by?: string;
  // True for an uploaded file no reader could open (sheet is ""). Listed on its own
  // line on the upload screen, never as an ignored sheet.
  unreadable?: boolean;
  // Tie-out warnings for a converted report that does not foot; the file is still
  // included. Absent when there are none.
  warnings?: string[];
}
